/**
 * AdTech forensic-flag / quality reference dialog (Tracer AskSentinel parity).
 *
 * Explains commercial mobile-location forensic flags, accuracy decoding, and
 * recommended filters. Optional onApplyFlagFilter wires FILTER chips into the
 * AdTech results table search box.
 */
import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SensorsIcon from '@mui/icons-material/Sensors';

export interface AdtechInfoDialogProps {
  open: boolean;
  onClose: () => void;
  /** Apply a forensic flag name as a results-table filter (e.g. SPOOF_LOCATION). */
  onApplyFlagFilter?: (flagName: string) => void;
}

const INFORMATIONAL_FLAGS = [
  { bit: 8, name: 'TOO_MANY_DEVICES_AT_LOCATION', desc: 'Many devices with same lat/lon. May warrant additional analysis.', color: '#2196f3' },
  { bit: 10, name: 'RADIO_DERIVED', desc: 'Location from cell tower triangulation vs GPS/WiFi.', color: '#ff9800' },
  { bit: 12, name: 'EU', desc: 'Device spends majority of time in EU.', color: '#9c27b0' },
  { bit: 14, name: 'LIKELY_DRIVING', desc: 'Movement consistent with vehicle/train travel.', color: '#4caf50' },
  { bit: 15, name: 'HIGH_ACCURACY', desc: 'Accuracy: 0-35 meters.', color: '#4caf50' },
  { bit: 16, name: 'MODERATE_ACCURACY', desc: 'Accuracy: 50-220 meters.', color: '#ff9800' },
  { bit: 17, name: 'LOW_ACCURACY', desc: 'Accuracy: 250-10,000 meters.', color: '#f44336' },
  { bit: 19, name: 'MOBILE_NETWORK', desc: 'Signal gathered on mobile network (U.S. only).', color: '#2196f3' },
  { bit: 25, name: 'US', desc: 'Device spends majority of time in U.S.', color: '#1976d2' },
  { bit: 32, name: 'IP_DERIVED', desc: 'Location from IP geolocation, not GPS.', color: '#ff9800' },
];

const QUALITY_FLAGS = [
  { bit: 7, name: 'LAT_GRID_LOCATION', desc: 'Location on grid coordinates. Likely estimated, low precision.', color: '#f44336', filter: true },
  { bit: 9, name: 'SPOOF_LOCATION', desc: 'Location appears spoofed. Filter for analytics.', color: '#d32f2f', filter: true },
  { bit: 13, name: 'OVER_CAPACITY_DEVICE', desc: 'Suspicious device with abnormal signal density.', color: '#d32f2f', filter: true },
  { bit: 20, name: 'HYPERV_OUTSIDE_CLUSTER', desc: 'Moved too fast outside spatial-temporal cluster.', color: '#ff5722' },
  { bit: 21, name: 'HYPERV_WITHIN_CLUSTER', desc: 'Moved too fast within cluster.', color: '#ff5722' },
  { bit: 22, name: 'HYPERV_CLUSTER_INTERLEAVE', desc: 'Clusters far apart overlap in time.', color: '#ff5722' },
  { bit: 23, name: 'TIMESHIFT', desc: 'Observation may be shifted in time.', color: '#ff9800' },
  { bit: 27, name: 'IMPLAUSIBLE_MOVEMENT', desc: 'Impossible movement pattern detected.', color: '#d32f2f', filter: true },
  { bit: 31, name: 'DENSE_CIRCULAR_AREAS', desc: 'Signals form distinct circle on map.', color: '#ff5722' },
];

const ACCURACY_TABLE = [
  { low: 0, mod: 0, high: 0, result: 'Unavailable (>10,000m)' },
  { low: 1, mod: 0, high: 0, result: 'Low (250-10,000m)' },
  { low: 1, mod: 1, high: 0, result: 'Moderate-Low (220-250m)' },
  { low: 0, mod: 1, high: 0, result: 'Moderate (50-220m)' },
  { low: 0, mod: 1, high: 1, result: 'Moderate-High (35-50m)' },
  { low: 0, mod: 0, high: 1, result: 'High (0-35m)' },
];

const ACCENT = '#e91e63';

const accordionSx = {
  bgcolor: 'transparent',
  '&:before': { display: 'none' },
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const AdtechInfoDialog: React.FC<AdtechInfoDialogProps> = ({
  open,
  onClose,
  onApplyFlagFilter,
}) => {
  const [expanded, setExpanded] = useState<string | false>('quality');

  const handleFilter = (flagName: string) => {
    onApplyFlagFilter?.(flagName);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.25,
          pr: 1,
          background: `linear-gradient(90deg, ${alpha(ACCENT, 0.18)}, ${alpha(ACCENT, 0.04)})`,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <SensorsIcon sx={{ color: ACCENT, fontSize: 22 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            AdTech Reference
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Commercial mobile-location forensic flags & quality guidance
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'about'}
          onChange={(_, v) => setExpanded(v ? 'about' : false)}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 40 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: ACCENT }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>About AdTech</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 1 }}>
              AdTech pulls commercial mobile-location observations (Venntel / GeoNeutron)
              for a drawn area or geofence. Results are not persisted — they live only in
              this session&apos;s map layer and results table.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.55 }}>
              <li>Area Search / Geofence Near query returns raw signals with carrier, IP, timezone</li>
              <li>Pink map dots follow the timeslider window</li>
              <li>Load PoL fetches full track history for selected registration IDs</li>
              <li>Forensic flags encode accuracy, network source, and quality risks</li>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'info'}
          onChange={(_, v) => setExpanded(v ? 'info' : false)}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 40 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <FlagIcon sx={{ fontSize: 16, color: '#2196f3' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Informational Flags</Typography>
              <Chip label={INFORMATIONAL_FLAGS.length} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1, px: 2, maxHeight: 280, overflow: 'auto' }}>
            {INFORMATIONAL_FLAGS.map((flag) => (
              <Box key={flag.bit} sx={{ mb: 1, pl: 0.75, borderLeft: `2px solid ${flag.color}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={flag.bit}
                    size="small"
                    sx={{ height: 16, fontSize: '0.6rem', bgcolor: alpha(flag.color, 0.15), color: flag.color }}
                  />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.2 }}>
                    {flag.name}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {flag.desc}
                </Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>

        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'quality'}
          onChange={(_, v) => setExpanded(v ? 'quality' : false)}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 40 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: '#f44336' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Quality Indicators</Typography>
              <Chip
                label={QUALITY_FLAGS.length}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem', ml: 0.5, bgcolor: alpha('#f44336', 0.15), color: '#f44336' }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1, px: 2, maxHeight: 300, overflow: 'auto' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.65rem' }}>
              Consider filtering these for precise analytics.
            </Typography>
            {QUALITY_FLAGS.map((flag) => (
              <Box key={flag.bit} sx={{ mb: 1, pl: 0.75, borderLeft: `2px solid ${flag.color}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={flag.bit}
                    size="small"
                    sx={{ height: 16, fontSize: '0.6rem', bgcolor: alpha(flag.color, 0.15), color: flag.color }}
                  />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{flag.name}</Typography>
                  {flag.filter && (
                    <Chip
                      label="FILTER"
                      size="small"
                      clickable={!!onApplyFlagFilter}
                      onClick={() => handleFilter(flag.name)}
                      sx={{
                        height: 16,
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        bgcolor: '#d32f2f',
                        color: 'white',
                        ml: 'auto',
                        cursor: onApplyFlagFilter ? 'pointer' : 'default',
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {flag.desc}
                </Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>

        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'accuracy'}
          onChange={(_, v) => setExpanded(v ? 'accuracy' : false)}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 40 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <GpsFixedIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Accuracy Reference</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.65rem' }}>
              Accuracy bands are derived from HIGH / MODERATE / LOW forensic flag bits when a
              raw accuracy value is not present.
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, py: 0.5, px: 0.75 }}>LOW</TableCell>
                    <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, py: 0.5, px: 0.75 }}>MOD</TableCell>
                    <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, py: 0.5, px: 0.75 }}>HIGH</TableCell>
                    <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, py: 0.5, px: 0.75 }}>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ACCURACY_TABLE.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ py: 0.35, px: 0.75 }}>
                        <Chip
                          label={row.low}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.55rem',
                            bgcolor: row.low ? alpha('#f44336', 0.15) : 'transparent',
                            color: row.low ? '#f44336' : 'text.disabled',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 0.75 }}>
                        <Chip
                          label={row.mod}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.55rem',
                            bgcolor: row.mod ? alpha('#ff9800', 0.15) : 'transparent',
                            color: row.mod ? '#ff9800' : 'text.disabled',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.35, px: 0.75 }}>
                        <Chip
                          label={row.high}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.55rem',
                            bgcolor: row.high ? alpha('#4caf50', 0.15) : 'transparent',
                            color: row.high ? '#4caf50' : 'text.disabled',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.65rem', py: 0.35, px: 0.75 }}>{row.result}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'practices'}
          onChange={(_, v) => setExpanded(v ? 'practices' : false)}
          sx={{ ...accordionSx, borderBottom: 0 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 40 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Best Practices</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#f44336', mb: 0.5 }}>
              Filter for analytics:
            </Typography>
            <Box sx={{ mb: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['SPOOF_LOCATION', 'OVER_CAPACITY_DEVICE', 'IMPLAUSIBLE_MOVEMENT'].map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  clickable={!!onApplyFlagFilter}
                  onClick={() => onApplyFlagFilter && handleFilter(name)}
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
              ))}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#ff9800', mb: 0.5 }}>
              Also filter for place visits:
            </Typography>
            <Box sx={{ mb: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['LAT_GRID_LOCATION', 'LIKELY_DRIVING'].map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  clickable={!!onApplyFlagFilter}
                  onClick={() => onApplyFlagFilter && handleFilter(name)}
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
              ))}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#4caf50', mb: 0.5 }}>
              Useful for commuting:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label="LIKELY_DRIVING"
                size="small"
                clickable={!!onApplyFlagFilter}
                onClick={() => onApplyFlagFilter && handleFilter('LIKELY_DRIVING')}
                sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha('#4caf50', 0.15), color: '#4caf50' }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
    </Dialog>
  );
};

export default AdtechInfoDialog;
